/**
 * BetCardV2.jsx — v2 results card for the new UI (feature-flagged)
 *
 * Design goals:
 *   - Taller, more readable 2-row collapsed layout: logos + selection/market row,
 *     then game context + line + odds + EV row
 *   - Expand on tap to reveal full per-sportsbook breakdown (sorted by EV)
 *     AND an EV math summary (fair prob, devig methods, Kelly)
 *   - Alt-line tab strip for grouped player/team props
 *   - Bet-slip add button separate from the expansion toggle
 *   - No new external deps — reads from the same `option` shape as UnifiedBetCard
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getSportsbookLogo } from '../data/logos';
import { useConsensusOptions } from '../utils/useConsensusOptions';
import { extractSummary, extractPrice, canonBook, resolveDeepLink } from '../utils/betCardV2Summary';
import * as betMath from '../utils/betMath';

// ─── Formatting helpers ────────────────────────────────────────────────────────

const fmtOdds = (price) => {
  if (price === null || price === undefined || isNaN(price)) return '—';
  const n = Number(price);
  return n > 0 ? `+${n}` : `${n}`;
};

const fmtEv = (ev) => {
  if (ev === null || ev === undefined || isNaN(ev)) return null;
  return `${ev > 0 ? '+' : ''}${Number(ev).toFixed(1)}%`;
};

const fmtPct = (p) => {
  if (p === null || p === undefined || isNaN(p)) return '—';
  return `${(Number(p) * 100).toFixed(1)}%`;
};

const evColor = (ev) => {
  if (ev === null || ev === undefined) return 'rgba(255,255,255,0.35)';
  if (ev >= 4) return '#10B981';
  if (ev >= 2) return '#84cc16';
  if (ev >= 0) return '#F59E0B';
  return '#F87171';
};

const evBgColor = (ev) => {
  if (ev === null || ev === undefined) return 'rgba(255,255,255,0.05)';
  if (ev >= 4) return 'rgba(16,185,129,0.14)';
  if (ev >= 2) return 'rgba(132,204,22,0.13)';
  if (ev >= 0) return 'rgba(245,158,11,0.11)';
  return 'rgba(248,113,113,0.11)';
};

const fmtTime = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();
    const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ' ' + timeStr;
  } catch { return null; }
};

// extractSummary / extractPrice / canonBook live in ../utils/betCardV2Summary
// (pure module, unit-tested) — all EV numbers flow through utils/canonicalEv.

// ─── Sportsbook row ────────────────────────────────────────────────────────────

function BookRow({ book, odds, ev, isMyBook, onOpenDeepLink }) {
  const price = extractPrice(odds);
  const deepLink = resolveDeepLink(odds);
  const logoUri = getSportsbookLogo?.(book);

  return (
    <View style={bStyles.bookRow}>
      <View style={bStyles.bookNameWrap}>
        {logoUri ? (
          <Image source={{ uri: logoUri }} style={bStyles.bookLogo} />
        ) : (
          <View style={bStyles.bookLogoFallback}>
            <Text style={bStyles.bookLogoFallbackText}>{(book || '?')[0].toUpperCase()}</Text>
          </View>
        )}
        <Text style={[bStyles.bookName, isMyBook && bStyles.bookNameMine]} numberOfLines={1}>
          {book}
        </Text>
        {isMyBook && (
          <View style={bStyles.myBookBadge}><Ionicons name="star" size={10} color="#A259FF" /></View>
        )}
      </View>

      <Text style={[bStyles.bookOdds, price !== null && price > 0 && bStyles.bookOddsPositive]}>
        {fmtOdds(price)}
      </Text>

      {ev !== null ? (
        <Text style={[bStyles.bookEv, { color: evColor(ev) }]}>{fmtEv(ev)}</Text>
      ) : (
        <Text style={bStyles.bookEvEmpty}>—</Text>
      )}

      {deepLink ? (
        <TouchableOpacity
          onPress={() => onOpenDeepLink?.(odds, book)}
          style={bStyles.bookLinkBtn}
          activeOpacity={0.7}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
        >
          <Ionicons name="open-outline" size={14} color="rgba(255,255,255,0.5)" />
        </TouchableOpacity>
      ) : (
        <View style={bStyles.bookLinkPlaceholder} />
      )}
    </View>
  );
}

// ─── Line tab (for grouped alt lines) ─────────────────────────────────────────

function LineTabs({ lines, selectedIdx, onSelect }) {
  if (!lines || lines.length <= 1) return null;
  return (
    <View style={bStyles.lineTabs}>
      {lines.map((line, i) => {
        const lv = line?.lineValue ?? line?.raw?.selection_line ?? line?.raw?.handicap ?? null;
        const isActive = i === selectedIdx;
        return (
          <TouchableOpacity
            key={i}
            onPress={() => onSelect(i)}
            activeOpacity={0.75}
            style={[bStyles.lineTab, isActive && bStyles.lineTabActive]}
          >
            <Text style={[bStyles.lineTabText, isActive && bStyles.lineTabTextActive]}>
              {lv !== null ? (Number(lv) > 0 ? `+${lv}` : `${lv}`) : `Line ${i + 1}`}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ─── EV pill ──────────────────────────────────────────────────────────────────

function EvPill({ ev }) {
  if (ev === null || ev === undefined) return null;
  return (
    <View style={[bStyles.evPill, { backgroundColor: evBgColor(ev) }]}>
      <Text style={[bStyles.evPillText, { color: evColor(ev) }]}>{fmtEv(ev)}</Text>
    </View>
  );
}

// ─── EV Math section (shown when expanded) ────────────────────────────────────

function EvMathSection({ summary }) {
  const { topEv, fairProb, impliedProb, devigSnapshot, bestPrice } = summary;

  // ¼ Kelly via canonical core (fraction=0.25 matches legacy display: fullKelly × 25 → quarter %)
  const kellyQuarter = useMemo(() => {
    if (fairProb === null || bestPrice === null) return null;
    return betMath.kellyFraction(fairProb, bestPrice, 0.25);
  }, [fairProb, bestPrice]);

  // Devig method rows from the canonical snapshot (same math UnifiedBetCard's
  // math block renders) — fair prob per method, computed at best price.
  const devigRows = useMemo(() => {
    if (!devigSnapshot) return [];
    const order = [
      ['power', 'Power'],
      ['multiplicative', 'Multiplicative'],
      ['additive', 'Additive'],
    ];
    return order
      .filter(([key]) => devigSnapshot[key]?.fairProb != null)
      .map(([key, label]) => ({
        label,
        value: devigSnapshot[key].fairProb,
        ev: devigSnapshot[key].ev,
      }));
  }, [devigSnapshot]);

  const hasAnyMath = topEv !== null || fairProb !== null || impliedProb !== null;
  if (!hasAnyMath) return null;

  return (
    <View style={bStyles.mathSection}>
      <View style={bStyles.mathHeaderRow}>
        <Ionicons name="calculator-outline" size={12} color="rgba(255,255,255,0.35)" />
        <Text style={bStyles.mathHeaderText}>EV Math</Text>
      </View>

      <View style={bStyles.mathGrid}>
        {topEv !== null && (
          <View style={bStyles.mathCell}>
            <Text style={bStyles.mathCellLabel}>Best EV</Text>
            <Text style={[bStyles.mathCellValue, { color: evColor(topEv) }]}>{fmtEv(topEv)}</Text>
          </View>
        )}
        {fairProb !== null && (
          <View style={bStyles.mathCell}>
            <Text style={bStyles.mathCellLabel}>Fair Prob</Text>
            <Text style={bStyles.mathCellValue}>{fmtPct(fairProb)}</Text>
          </View>
        )}
        {impliedProb !== null && (
          <View style={bStyles.mathCell}>
            <Text style={bStyles.mathCellLabel}>Implied</Text>
            <Text style={bStyles.mathCellValue}>{fmtPct(impliedProb)}</Text>
          </View>
        )}
        {kellyQuarter !== null && (
          <View style={bStyles.mathCell}>
            <Text style={bStyles.mathCellLabel}>¼ Kelly</Text>
            <Text style={[bStyles.mathCellValue, { color: '#a78bfa' }]}>
              {(kellyQuarter * 100).toFixed(1)}%
            </Text>
          </View>
        )}
      </View>

      {devigRows.length > 0 && (
        <View style={bStyles.devigRows}>
          <Text style={bStyles.devigTitle}>Devig Methods</Text>
          {devigRows.map(({ label, value, ev }) => (
            <View key={label} style={bStyles.devigRow}>
              <Text style={bStyles.devigLabel}>{label}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={bStyles.devigValue}>{fmtPct(value)}</Text>
                {ev !== null && ev !== undefined && !isNaN(ev) && (
                  <Text style={[bStyles.devigValue, { color: evColor(ev) }]}>{fmtEv(ev)}</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

// ─── Main card ────────────────────────────────────────────────────────────────

export default function BetCardV2({
  option,
  groupedLines = null,
  isSelected = false,
  onSelect,
  onOpenDeepLink,
  myBooks = [],
  homeLogoUrl,
  awayLogoUrl,
  teamLogoUrl,
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeLineIdx, setActiveLineIdx] = useState(0);

  // User's consensus-exclusion policy — threaded into every canonicalEv call so
  // this card's EVs match the results sort and UnifiedBetCard under the same policy.
  const consensusOptions = useConsensusOptions();

  const workingOption = useMemo(() => {
    if (groupedLines && groupedLines.length > 0) {
      return groupedLines[activeLineIdx] || groupedLines[0];
    }
    return option;
  }, [option, groupedLines, activeLineIdx]);

  const summary = useMemo(
    () => extractSummary(workingOption, consensusOptions),
    [workingOption, consensusOptions]
  );

  const myBooksNorm = useMemo(
    () => new Set((myBooks || []).map(b => canonBook(b).toLowerCase())),
    [myBooks]
  );

  const isMyBook = useCallback(
    (book) => myBooksNorm.has(canonBook(book).toLowerCase()),
    [myBooksNorm]
  );

  const gameLabel = useMemo(() => {
    const teams = [summary.awayTeam, summary.homeTeam].filter(Boolean);
    const matchup = teams.length >= 2 ? `${teams[0]} @ ${teams[1]}` : teams[0] || '';
    const time = fmtTime(summary.commenceTime);
    return [matchup, time].filter(Boolean).join(' · ');
  }, [summary]);

  const marketLabel = useMemo(() => {
    const m = summary.market || '';
    return m
      .replace('Player Points', 'Points')
      .replace('Player Rebounds', 'Rebounds')
      .replace('Player Assists', 'Assists')
      .replace('Player Threes Made', '3PM')
      .replace('Player Passing Yards', 'Pass Yds')
      .replace('Player Rushing Yards', 'Rush Yds')
      .replace('Player Receiving Yards', 'Rec Yds')
      .replace('Point Spread', 'Spread')
      .replace('Total Points', 'Total')
      .replace('Moneyline', 'ML');
  }, [summary.market]);

  const lineDisplay = useMemo(() => {
    const sel = (summary.selection || '').toLowerCase();
    const lv = summary.lineVal;
    if (lv === null || lv === undefined) return null;
    if (/\bover\b/i.test(sel)) return `O ${lv}`;
    if (/\bunder\b/i.test(sel)) return `U ${lv}`;
    const sign = Number(lv) > 0 ? `+${lv}` : `${lv}`;
    return sign;
  }, [summary]);

  const logoToShow = teamLogoUrl || homeLogoUrl || null;
  const awayLogo = awayLogoUrl || null;
  const hasGroups = groupedLines && groupedLines.length > 1;

  return (
    <View style={[bStyles.card, isSelected && bStyles.cardSelected]}>

      {/* ── Collapsed header (tap to expand) ──────────────────────────── */}
      <TouchableOpacity
        onPress={() => setExpanded(e => !e)}
        activeOpacity={0.85}
        style={bStyles.headerTouchable}
      >
        {/* ── Row 1: logos + selection + market + add + chevron ─────── */}
        <View style={bStyles.row1}>
          {/* Logos */}
          <View style={bStyles.logoWrap}>
            {awayLogo && homeLogoUrl ? (
              <>
                <Image source={{ uri: awayLogo }} style={bStyles.logoSm} />
                <Image source={{ uri: homeLogoUrl }} style={[bStyles.logoSm, bStyles.logoOverlap]} />
              </>
            ) : logoToShow ? (
              <Image source={{ uri: logoToShow }} style={bStyles.logo} />
            ) : (
              <View style={bStyles.logoPlaceholder}>
                <Ionicons name="trophy-outline" size={16} color="rgba(255,255,255,0.25)" />
              </View>
            )}
          </View>

          {/* Selection text */}
          <View style={bStyles.selectionCol}>
            <Text style={bStyles.selectionText} numberOfLines={2}>
              {summary.selection || marketLabel}
            </Text>
          </View>

          {/* Market badge */}
          <View style={bStyles.marketBadge}>
            <Text style={bStyles.marketBadgeText} numberOfLines={1}>{marketLabel}</Text>
          </View>

          {/* Add to slip button */}
          <TouchableOpacity
            onPress={(e) => { e.stopPropagation?.(); onSelect?.(workingOption); }}
            style={[bStyles.addBtn, isSelected && bStyles.addBtnSelected]}
            activeOpacity={0.75}
            hitSlop={{ top: 6, bottom: 6, left: 4, right: 8 }}
          >
            <Ionicons
              name={isSelected ? 'checkmark' : 'add'}
              size={16}
              color={isSelected ? '#A259FF' : 'rgba(255,255,255,0.6)'}
            />
          </TouchableOpacity>

          {/* Expand chevron */}
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={14}
            color="rgba(255,255,255,0.25)"
          />
        </View>

        {/* ── Row 2: game label + line + odds + EV + book count ─────── */}
        <View style={bStyles.row2}>
          {gameLabel ? (
            <Text style={bStyles.gameLabelText} numberOfLines={1}>{gameLabel}</Text>
          ) : (
            <View style={{ flex: 1 }} />
          )}

          <View style={bStyles.row2Right}>
            {lineDisplay ? (
              <View style={bStyles.lineChip}>
                <Text style={bStyles.lineChipText}>{lineDisplay}</Text>
              </View>
            ) : null}

            <Text style={[
              bStyles.oddsText,
              summary.bestPrice !== null && summary.bestPrice > 0 && bStyles.oddsTextPositive,
            ]}>
              {fmtOdds(summary.bestPrice)}
            </Text>

            <EvPill ev={summary.topEv} />

            <View style={bStyles.booksChip}>
              <Ionicons name="bookmark-outline" size={10} color="rgba(255,255,255,0.35)" />
              <Text style={bStyles.booksChipText}>{summary.bookCount}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>

      {/* ── Expanded detail ──────────────────────────────────────────── */}
      {expanded && (
        <View style={bStyles.expandedWrap}>
          {/* Alt-line tabs */}
          {hasGroups && (
            <LineTabs
              lines={groupedLines}
              selectedIdx={activeLineIdx}
              onSelect={setActiveLineIdx}
            />
          )}

          {/* Sportsbooks header */}
          <View style={bStyles.booksHeader}>
            <Text style={bStyles.booksHeaderBook}>Sportsbook</Text>
            <Text style={bStyles.booksHeaderOdds}>Odds</Text>
            <Text style={bStyles.booksHeaderEv}>EV%</Text>
            <View style={bStyles.bookLinkPlaceholder} />
          </View>

          {/* Book rows */}
          {summary.books.map(({ book, odds, ev }) => (
            <BookRow
              key={book}
              book={book}
              odds={odds}
              ev={ev}
              isMyBook={isMyBook(book)}
              onOpenDeepLink={onOpenDeepLink}
            />
          ))}

          {summary.bookCount === 0 && (
            <Text style={bStyles.noBooksText}>No sportsbook data available</Text>
          )}

          {/* EV Math section */}
          <EvMathSection summary={summary} />
        </View>
      )}
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const bStyles = StyleSheet.create({
  card: {
    backgroundColor: 'rgba(255,255,255,0.035)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 14,
    marginBottom: 10,
    overflow: 'hidden',
  },
  cardSelected: {
    borderColor: 'rgba(162,89,255,0.45)',
    backgroundColor: 'rgba(162,89,255,0.05)',
  },

  // ── Tap area wraps both rows ──────────────────────────────────────────────
  headerTouchable: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 12,
    gap: 8,
  },

  // ── Row 1: logos + selection + market + add + chevron ────────────────────
  row1: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },

  // ── Row 2: game context + line + odds + EV + books ───────────────────────
  row2: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 38, // aligns under selection text (past logo)
  },
  row2Right: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginLeft: 'auto',
    flexShrink: 0,
  },

  // Logos
  logoWrap: {
    width: 36,
    height: 28,
    position: 'relative',
    flexShrink: 0,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  logoSm: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.06)',
    position: 'absolute',
    top: 0,
  },
  logoOverlap: {
    left: 14,
    top: 6,
    borderWidth: 1.5,
    borderColor: 'rgba(10,10,10,0.9)',
  },
  logoPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Selection
  selectionCol: {
    flex: 1,
    minWidth: 0,
  },
  selectionText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.1,
    lineHeight: 19,
  },

  // Market badge
  marketBadge: {
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexShrink: 0,
    maxWidth: 120,
  },
  marketBadgeText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    fontWeight: '600',
  },

  // Game label (row 2 left)
  gameLabelText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11.5,
    fontWeight: '400',
    flex: 1,
    minWidth: 0,
  },

  // Line chip
  lineChip: {
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: 5,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  lineChipText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 12,
    fontWeight: '700',
  },

  // Odds
  oddsText: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 15,
    fontWeight: '800',
    flexShrink: 0,
    minWidth: 46,
    textAlign: 'right',
  },
  oddsTextPositive: {
    color: '#86efac',
  },

  // EV pill
  evPill: {
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexShrink: 0,
  },
  evPillText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.2,
  },

  // Books chip
  booksChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
  },
  booksChipText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    fontWeight: '500',
  },

  // Add to slip button
  addBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addBtnSelected: {
    borderColor: 'rgba(162,89,255,0.5)',
    backgroundColor: 'rgba(162,89,255,0.1)',
  },

  // ── Expanded section ──────────────────────────────────────────────────────
  expandedWrap: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 6,
  },

  // Line tabs
  lineTabs: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.06)',
    marginBottom: 6,
  },
  lineTab: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  lineTabActive: {
    borderColor: 'rgba(162,89,255,0.55)',
    backgroundColor: 'rgba(162,89,255,0.12)',
  },
  lineTabText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 12,
    fontWeight: '500',
  },
  lineTabTextActive: {
    color: '#c4b5fd',
    fontWeight: '700',
  },

  // Books table
  booksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
    marginBottom: 2,
    gap: 8,
  },
  booksHeaderBook: {
    flex: 1,
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  booksHeaderOdds: {
    width: 52,
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'right',
  },
  booksHeaderEv: {
    width: 52,
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'right',
  },

  // Book row
  bookRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.04)',
    gap: 8,
  },
  bookNameWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    minWidth: 0,
  },
  bookLogo: {
    width: 20,
    height: 20,
    borderRadius: 5,
    backgroundColor: 'rgba(255,255,255,0.05)',
    flexShrink: 0,
  },
  bookLogoFallback: {
    width: 20,
    height: 20,
    borderRadius: 5,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bookLogoFallbackText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 9,
    fontWeight: '700',
  },
  bookName: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 12.5,
    fontWeight: '500',
    flex: 1,
  },
  bookNameMine: {
    color: '#c4b5fd',
    fontWeight: '700',
  },
  myBookBadge: {
    backgroundColor: 'rgba(162,89,255,0.15)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  myBookBadgeText: {
    color: '#A259FF',
    fontSize: 9,
    fontWeight: '800',
  },
  bookOdds: {
    width: 52,
    color: 'rgba(255,255,255,0.75)',
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'right',
  },
  bookOddsPositive: {
    color: '#86efac',
  },
  bookEv: {
    width: 52,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'right',
  },
  bookEvEmpty: {
    width: 52,
    color: 'rgba(255,255,255,0.2)',
    fontSize: 12,
    textAlign: 'right',
  },
  bookLinkBtn: {
    width: 30,
    height: 30,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  bookLinkPlaceholder: {
    width: 30,
    flexShrink: 0,
  },

  noBooksText: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 12,
    textAlign: 'center',
    paddingVertical: 14,
  },

  // ── EV Math section ───────────────────────────────────────────────────────
  mathSection: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.06)',
    paddingTop: 10,
  },
  mathHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 8,
  },
  mathHeaderText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  mathGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  mathCell: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    minWidth: 72,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
  },
  mathCellLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 3,
  },
  mathCellValue: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '800',
  },

  // Devig methods
  devigRows: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    gap: 6,
  },
  devigTitle: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  devigRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  devigLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 11.5,
    fontWeight: '500',
  },
  devigValue: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
    fontWeight: '700',
  },
});
